/**
 * Which organization "door" the current page is.
 *
 * Every organization gets path-prefixed auth pages:
 *
 *     /sign-in              /register              /admin/sign-in       → BusinessLocating
 *     /ecfa/sign-in         /ecfa/register         /ecfa/admin/sign-in  → eCashflow Acquisitions
 *
 * The slug is read off the URL rather than threaded through every auth screen
 * as a prop — the auth pages are shared components rendered by several routes,
 * and the door is a property of the URL, not of the form.
 *
 * The backend re-resolves this slug against the orgs table and rejects unknown
 * ones (see app/routers/auth.py::_resolve_org), so a hand-typed prefix can't
 * create accounts anywhere. This helper is convenience, never a security check.
 */

/** Paths that follow an org slug on a door URL. */
const DOOR_SUFFIXES = ["sign-in", "register", "admin/sign-in"];

export function orgSlugFromPath(pathname: string = window.location.pathname): string | null {
  const segments = pathname.split("/").filter(Boolean);
  // "/sign-in" and "/register" — the main site's own doors.
  if (segments.length < 2) return null;
  // "/admin/sign-in" is BusinessLocating's admin door, not an org named "admin".
  if (segments[0] === "admin") return null;
  const suffix = segments.slice(1).join("/").toLowerCase();
  return DOOR_SUFFIXES.includes(suffix) ? segments[0].toLowerCase() : null;
}

/** Prefix a door path with the current org, e.g. "/sign-in" → "/ecfa/sign-in". */
export function orgDoorPath(path: string, slug: string | null = orgSlugFromPath()): string {
  if (!slug) return path;
  return `/${slug}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * The door this browser last signed in through.
 *
 * Needed because a session that expires has no user object to read org_id
 * from, and cross-door logins are rejected with an error deliberately
 * identical to a wrong password. Without this, an expired ECFA admin would be
 * sent to BusinessLocating's door, told "invalid credentials", and have no way
 * to work out why their correct password wasn't working.
 */
const DOOR_KEY = "org_door_slug";

export function rememberOrgDoor(slug: string | null): void {
  try {
    if (slug) localStorage.setItem(DOOR_KEY, slug);
    else localStorage.removeItem(DOOR_KEY);
  } catch {
    /* storage unavailable — falls back to the main door */
  }
}

export function rememberedOrgDoor(): string | null {
  try {
    return localStorage.getItem(DOOR_KEY);
  } catch {
    return null;
  }
}

/**
 * Prefix an in-app path with the signed-in user's organization.
 *
 * Admins of a non-default organization work under `/<slug>/admin/...` so the
 * URL says which organization's console they are in. Members of the default
 * organization keep the bare paths.
 *
 * This is presentation only — it grants nothing. Every admin endpoint scopes
 * its data by the caller's own `org_id` server-side (see admin.org_scope), so
 * editing the slug in the address bar changes what the URL reads, never what
 * the API returns.
 */
export function orgScopedPath(path: string, orgSlug: string | null | undefined): string {
  return orgSlug ? `/${orgSlug}${path}` : path;
}
